import { useMemo } from 'react';
import { useOrganizationContext } from '../../../shared/contexts/OrganizationContext';
import { 
  hasMinimumRole, 
  type OrganizationMemberRole 
} from '../../../shared/types/organization.types';

interface UseOrgPermissionsResult {
  // Current role
  role: OrganizationMemberRole | null;
  
  // Permission checks
  canView: boolean;
  canEdit: boolean;
  canManage: boolean;
  canDelete: boolean;
  isOwner: boolean;
  
  // System admin overrides
  isSystemAdmin: boolean;
  
  // Helpers
  hasRole: (requiredRole: OrganizationMemberRole) => boolean;
  requireRole: (requiredRole: OrganizationMemberRole) => boolean;
}

/**
 * Hook to check permissions for the current user in the active organization.
 * System admins automatically have all permissions.
 * 
 * Usage:
 * const { canEdit, canManage, hasRole } = useOrgPermissions();
 * if (!canEdit) return <AccessDenied />;
 */
export function useOrgPermissions(): UseOrgPermissionsResult {
  const { activeRole, isSystemAdmin } = useOrganizationContext();

  const permissions = useMemo(() => {
    // System admin has all permissions
    if (isSystemAdmin) {
      return {
        role: activeRole,
        canView: true,
        canEdit: true,
        canManage: true,
        canDelete: true,
        isOwner: true,
        isSystemAdmin: true,
        hasRole: () => true,
        requireRole: () => true,
      };
    }

    // No role means no permissions
    if (!activeRole) {
      return {
        role: null,
        canView: false,
        canEdit: false,
        canManage: false,
        canDelete: false,
        isOwner: false,
        isSystemAdmin: false,
        hasRole: () => false,
        requireRole: () => false,
      };
    }

    const hasRole = (requiredRole: OrganizationMemberRole) => 
      hasMinimumRole(activeRole, requiredRole);

    return {
      role: activeRole,
      canView: hasRole('viewer'),
      canEdit: hasRole('member'),
      canManage: hasRole('admin'),
      canDelete: hasRole('owner'),
      isOwner: activeRole === 'owner',
      isSystemAdmin: false,
      hasRole,
      requireRole: hasRole,
    };
  }, [activeRole, isSystemAdmin]);

  return permissions;
}

/**
 * Hook to check if user has access to a specific organization by ID.
 * Returns the user's role in that organization.
 */
export function useOrgAccess(organizationId: string | null): {
  hasAccess: boolean;
  role: OrganizationMemberRole | null;
  isLoading: boolean;
} {
  const { userContext, isLoading, isSystemAdmin } = useOrganizationContext();

  const access = useMemo(() => {
    if (isSystemAdmin) {
      return { hasAccess: true, role: 'owner' as OrganizationMemberRole };
    }

    if (!organizationId || !userContext) {
      return { hasAccess: false, role: null };
    }

    const membership = userContext.organizations.find(
      m => m.organization.id === organizationId
    );

    return {
      hasAccess: !!membership,
      role: membership?.role || null,
    };
  }, [organizationId, userContext, isSystemAdmin]);

  return {
    ...access,
    isLoading,
  };
}
